import React, { useEffect, useState } from "react";
import { AdminLayout } from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import * as Lucide from "lucide-react";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis } from "recharts";

const COLORS = ["#8B5CF6", "#06B6D4", "#3B82F6", "#10B981", "#F59E0B", "#EC4899", "#A855F7"];

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bx-card p-5">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</div>
        <span className={`bx-glow-icon ${accent}`}><Icon size={18} /></span>
      </div>
      <div className="bx-heading mt-2 text-3xl font-extrabold">{value}</div>
    </div>
  );
}

function TapToPayTile({ data }) {
  const lt = data?.lifetime || {};
  const d24 = data?.last_24h || {};
  const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const rateClass = (pct) => pct >= 50 ? "text-emerald-300" : pct >= 20 ? "text-amber-300" : "text-slate-400";
  return (
    <div className="bx-card p-5 mt-6" data-testid="tap-to-pay-tile" style={{ background: "linear-gradient(135deg, rgba(139,92,246,0.06), rgba(59,130,246,0.04))" }}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="bx-heading text-lg font-bold flex items-center gap-2">
            <Lucide.Smartphone size={18} className="text-purple-300" /> Tap-to-Pay Conversion
          </div>
          <p className="text-xs text-slate-400 mt-1">Per-link UPI checkout · taps, paid, and ROI across all your pay links.</p>
        </div>
        <a href="/admin/upi" className="bx-btn-ghost text-xs"><Lucide.ExternalLink size={12} /> Open UPI panel</a>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
        <div className="rounded-xl p-4 border border-white/10 bg-white/[0.02]">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Links sent</div>
          <div className="text-2xl font-extrabold text-slate-100 mt-1">{lt.total_links || 0}</div>
          <div className="text-[10px] text-slate-500 mt-1">{lt.active_links || 0} active right now</div>
        </div>
        <div className="rounded-xl p-4 border border-white/10 bg-white/[0.02]">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Total taps</div>
          <div className="text-2xl font-extrabold text-purple-300 mt-1">{lt.taps || 0}</div>
          <div className={`text-[10px] mt-1 ${rateClass(lt.tap_to_pay_pct)}`}>{lt.tap_to_pay_pct || 0}% tap → pay</div>
        </div>
        <div className="rounded-xl p-4 border border-white/10 bg-white/[0.02]">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Paid</div>
          <div className="text-2xl font-extrabold text-emerald-300 mt-1">{lt.paid_count || 0}</div>
          <div className={`text-[10px] mt-1 ${rateClass(lt.conversion_pct)}`}>{lt.conversion_pct || 0}% link → paid</div>
        </div>
        <div className="rounded-xl p-4 border border-white/10 bg-white/[0.02]">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">₹ Collected</div>
          <div className="text-2xl font-extrabold text-slate-100 mt-1">₹{fmt(lt.paid_amount)}</div>
          <div className="text-[10px] text-slate-500 mt-1">Lifetime via pay links</div>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-white/5 flex items-center gap-6 text-xs text-slate-400 flex-wrap">
        <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Last 24h</span>
        <span><strong className="text-slate-100">{d24.total_links || 0}</strong> link{(d24.total_links || 0) === 1 ? "" : "s"} sent</span>
        <span><strong className="text-purple-300">{d24.taps || 0}</strong> tap{(d24.taps || 0) === 1 ? "" : "s"}</span>
        <span><strong className="text-emerald-300">{d24.paid_count || 0}</strong> paid · <strong>₹{fmt(d24.paid_amount)}</strong></span>
        <span className={rateClass(d24.conversion_pct)}><strong>{d24.conversion_pct || 0}%</strong> conversion</span>
      </div>
    </div>
  );
}

export function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [upiSummary, setUpiSummary] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/upi/conversion-summary").then((r) => setUpiSummary(r.data)).catch(() => setUpiSummary(null));
  }, []);
  if (!stats) return <AdminLayout><div className="text-slate-400">Loading…</div></AdminLayout>;

  const pieData = [
    { name: "Used", value: stats.credits_used },
    { name: "Remaining", value: Math.max(0, stats.credits_sold - stats.credits_used) },
  ];

  return (
    <AdminLayout>
      <h1 className="bx-heading text-3xl font-extrabold">Admin Overview</h1>
      <p className="text-slate-400 text-sm mt-1">Real-time snapshot of BINAREX.CO performance.</p>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-6">
        <Stat icon={Lucide.Users} label="Total Users" value={stats.total_users.toLocaleString()} accent="" />
        <Stat icon={Lucide.DollarSign} label="Total Sales" value={`$${stats.total_sales_usd.toFixed(2)}`} accent="emerald" />
        <Stat icon={Lucide.Coins} label="Credits Sold" value={stats.credits_sold.toLocaleString()} accent="cyan" />
        <Stat icon={Lucide.Activity} label="Credits Used" value={stats.credits_used.toLocaleString()} accent="amber" />
        <Stat icon={Lucide.BadgeCheck} label="Active Subs" value={stats.active_subscriptions.toLocaleString()} accent="blue" />
      </div>

      {upiSummary && <TapToPayTile data={upiSummary} />}

      <div className="grid lg:grid-cols-3 gap-5 mt-6">
        <div className="bx-card p-5">
          <div className="bx-heading text-lg font-semibold">Credits Overview</div>
          <div className="h-64 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={90} paddingAngle={3}>
                  {pieData.map((_, i) => (<Cell key={i} fill={COLORS[i % COLORS.length]} />))}
                </Pie>
                <Tooltip contentStyle={{ background: "#0B0F19", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="lg:col-span-2 bx-card p-5">
          <div className="bx-heading text-lg font-semibold">Top AI Tools by Usage</div>
          <div className="h-64 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.top_tools} layout="vertical" margin={{ left: 10, right: 20 }}>
                <XAxis type="number" stroke="#64748b" fontSize={11} />
                <YAxis dataKey="name" type="category" stroke="#94a3b8" fontSize={11} width={130} />
                <Tooltip contentStyle={{ background: "#0B0F19", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
                <Bar dataKey="count" radius={[0, 8, 8, 0]}>
                  {stats.top_tools.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

export function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [adjustUser, setAdjustUser] = useState(null);
  const [delta, setDelta] = useState(100);
  const [note, setNote] = useState("");
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const adjust = async () => {
    try {
      await api.post("/admin/credits/adjust", { user_id: adjustUser.id, delta: Number(delta), note });
      toast.success("Credits adjusted");
      setAdjustUser(null); setDelta(100); setNote(""); load();
    } catch { toast.error("Failed"); }
  };
  const del = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); }
  };

  return (
    <AdminLayout>
      <h1 className="bx-heading text-3xl font-extrabold">Users</h1>
      <div className="bx-card mt-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-xs uppercase tracking-wider text-slate-400">
            <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Email</th><th className="text-left p-3">Role</th><th className="text-left p-3">Plan</th><th className="text-right p-3">Credits</th><th className="text-left p-3">Joined</th><th className="text-right p-3">Actions</th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-white/5">
                <td className="p-3">{u.name}</td>
                <td className="p-3 text-slate-300">{u.email}</td>
                <td className="p-3"><span className="bx-chip" style={{ fontSize: 10 }}>{u.role}</span></td>
                <td className="p-3 text-slate-400 capitalize">{u.plan}</td>
                <td className="p-3 text-right font-mono text-purple-300">{u.credits?.toLocaleString()}</td>
                <td className="p-3 text-slate-500">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="p-3 text-right space-x-2">
                  <button data-testid={`admin-user-adjust-${u.id}`} onClick={() => setAdjustUser(u)} className="bx-btn-ghost text-xs">Adjust Credits</button>
                  {u.role !== "admin" && <button onClick={() => del(u)} className="bx-btn-ghost text-xs text-rose-300">Delete</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {adjustUser && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-5">
          <div className="bx-card p-6 max-w-md w-full">
            <div className="bx-heading text-lg font-bold">Adjust credits for {adjustUser.email}</div>
            <p className="text-xs text-slate-400 mt-1">Use a negative value to deduct.</p>
            <input type="number" value={delta} onChange={(e) => setDelta(e.target.value)} className="bx-input mt-3" />
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className="bx-input mt-3" />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setAdjustUser(null)} className="bx-btn-ghost">Cancel</button>
              <button onClick={adjust} className="bx-btn-primary">Apply</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export function AdminTools() {
  const [tools, setTools] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const load = () => api.get("/admin/tools").then((r) => setTools(r.data));
  const loadCats = () => api.get("/admin/categories").then((r) => setCategories(r.data));
  useEffect(() => { load(); loadCats(); }, []);
  const save = async () => {
    try { await api.patch(`/admin/tools/${editing.slug}`, editing); toast.success("Saved"); setEditing(null); load(); loadCats(); }
    catch { toast.error("Failed"); }
  };
  const createTool = async () => {
    try {
      await api.post("/admin/tools", editing);
      toast.success("Tool created");
      setEditing(null); setCreating(false);
      load(); loadCats();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const deleteTool = async (slug) => {
    if (!window.confirm(`Delete ${slug}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/tools/${slug}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };
  const startCreate = () => {
    setCreating(true);
    setEditing({ name: "", slug: "", description: "", category: categories[0]?.name || "Other", credit_cost: 5, enabled: true, system_prompt: "", icon: "Sparkles" });
  };
  const filtered = tools.filter((t) => {
    if (categoryFilter && t.category !== categoryFilter) return false;
    if (query && !`${t.name} ${t.slug} ${t.category}`.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });
  return (
    <AdminLayout>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="bx-heading text-3xl font-extrabold">AI Tools</h1>
          <p className="text-slate-400 text-sm mt-1">Set credit cost, enable/disable, manage categories, or add new tools.</p>
        </div>
        <button data-testid="admin-tool-create" onClick={startCreate} className="bx-btn-primary text-sm"><Lucide.Plus size={14} /> Add tool</button>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-4 text-xs">
        <button onClick={() => setCategoryFilter("")} className={`bx-chip ${categoryFilter === "" ? "ring-1 ring-purple-400" : ""}`}>All · {tools.length}</button>
        {categories.map((c) => (
          <button key={c.id} onClick={() => setCategoryFilter(c.name)} className={`bx-chip ${categoryFilter === c.name ? "ring-1 ring-purple-400" : ""}`} data-testid={`tools-cat-filter-${c.slug}`}>
            {c.name} · {c.tool_count}
          </button>
        ))}
        <div className="ml-auto relative">
          <Lucide.Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
          <input data-testid="admin-tools-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search tools…" className="bx-input pl-7 text-xs py-1.5 w-56" />
        </div>
      </div>
      <div className="bx-card mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-xs uppercase tracking-wider text-slate-400">
            <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Slug</th><th className="text-left p-3">Category</th><th className="text-right p-3">Cost</th><th className="text-left p-3">Enabled</th><th className="text-right p-3">Actions</th></tr>
          </thead>
          <tbody>
            {filtered.map((t) => (
              <tr key={t.id} className="border-t border-white/5">
                <td className="p-3">{t.name}</td>
                <td className="p-3 text-slate-400 bx-mono text-xs">{t.slug}</td>
                <td className="p-3 text-slate-400">{t.category}</td>
                <td className="p-3 text-right font-mono text-purple-300">{t.credit_cost}</td>
                <td className="p-3"><span className="bx-chip" style={{ fontSize: 10 }}>{t.enabled ? "active" : "disabled"}</span></td>
                <td className="p-3 text-right">
                  <button data-testid={`admin-tool-edit-${t.slug}`} onClick={() => { setCreating(false); setEditing({ ...t }); }} className="bx-btn-ghost text-xs"><Lucide.Pencil size={12} /></button>
                  <button data-testid={`admin-tool-delete-${t.slug}`} onClick={() => deleteTool(t.slug)} className="bx-btn-ghost text-xs text-rose-300 ml-1"><Lucide.Trash2 size={12} /></button>
                </td>
              </tr>
            ))}
            {!filtered.length && <tr><td colSpan={6} className="p-8 text-center text-slate-500 text-sm">No tools.</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-5">
          <div className="bx-card p-6 max-w-lg w-full">
            <div className="bx-heading text-lg font-bold">{creating ? "Add new tool" : `Edit ${editing.name}`}</div>
            <div className="mt-3 space-y-3">
              <input data-testid="admin-tool-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Name" className="bx-input" />
              {creating && <input data-testid="admin-tool-slug" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="Slug (auto-generated if blank)" className="bx-input bx-mono text-xs" />}
              <input value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="Description" className="bx-input" />
              <div className="flex gap-2">
                <select
                  data-testid="admin-tool-category"
                  value={editing.category}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "__new__") {
                      const name = window.prompt("New category name");
                      if (name) {
                        api.post("/admin/categories", { name }).then(() => { loadCats(); setEditing({ ...editing, category: name }); });
                      }
                    } else {
                      setEditing({ ...editing, category: v });
                    }
                  }}
                  className="bx-input flex-1"
                >
                  {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                  {!categories.some((c) => c.name === editing.category) && editing.category && <option value={editing.category}>{editing.category} (current)</option>}
                  <option value="__new__">+ Add new category…</option>
                </select>
                <input type="number" min="0" value={editing.credit_cost} onChange={(e) => setEditing({ ...editing, credit_cost: Number(e.target.value) })} placeholder="Cost" className="bx-input w-24" />
              </div>
              <textarea rows={4} value={editing.system_prompt || ""} onChange={(e) => setEditing({ ...editing, system_prompt: e.target.value })} placeholder="System prompt" className="bx-input resize-y" />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.enabled} onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} /> Enabled</label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => { setEditing(null); setCreating(false); }} className="bx-btn-ghost">Cancel</button>
              <button data-testid="admin-tool-save" onClick={creating ? createTool : save} className="bx-btn-primary">{creating ? "Create" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [tools, setTools] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => {
    api.get("/admin/plans").then((r) => setPlans(r.data));
    api.get("/admin/tools").then((r) => setTools(r.data));
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      // Normalize fields back to API shape
      const body = {
        ...editing,
        features: typeof editing.features === "string" ? editing.features.split("\n").filter(Boolean) : editing.features,
        tool_access: editing._toolAccessMode === "all" ? [] : (editing.tool_access || []),
        credit_overrides: editing.credit_overrides || {},
        monthly_credit_limit: editing.monthly_credit_limit ? Number(editing.monthly_credit_limit) : null,
      };
      delete body._toolAccessMode;
      delete body.id;
      await api.patch(`/admin/plans/${editing.slug}`, body);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openEdit = (p) => {
    const access = p.tool_access || [];
    setEditing({
      ...p,
      features: (p.features || []).join("\n"),
      tool_access: [...access],
      credit_overrides: { ...(p.credit_overrides || {}) },
      _toolAccessMode: access.length === 0 ? "all" : "include",
    });
  };

  const toggleToolAccess = (slug) => {
    setEditing((e) => {
      const set = new Set(e.tool_access || []);
      if (set.has(slug)) set.delete(slug); else set.add(slug);
      return { ...e, tool_access: Array.from(set) };
    });
  };

  return (
    <AdminLayout>
      <h1 className="bx-heading text-3xl font-extrabold">Pricing Plans</h1>
      <p className="text-slate-400 text-sm mt-1">Customize price, credits, included AI tools, per-tool credit overrides, and monthly credit caps for each plan.</p>

      {/* Audit table */}
      <div className="bx-card p-5 mt-6" data-testid="plans-audit-panel">
        <div className="flex items-center justify-between">
          <div className="bx-heading text-base font-bold">Plans Audit</div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Spot misconfigured plans at a glance</div>
        </div>
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-slate-500 border-b border-white/10">
                <th className="py-2 pr-3">Plan</th>
                <th className="py-2 pr-3">Price</th>
                <th className="py-2 pr-3">Credits</th>
                <th className="py-2 pr-3">Cr/$</th>
                <th className="py-2 pr-3">Tools</th>
                <th className="py-2 pr-3">Overrides</th>
                <th className="py-2 pr-3">Avg override</th>
                <th className="py-2 pr-3">Monthly cap</th>
                <th className="py-2 pr-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((p) => {
                const totalTools = tools.length || 0;
                const includedCount = (p.tool_access || []).length === 0 ? totalTools : p.tool_access.length;
                const overrides = p.credit_overrides || {};
                const ovKeys = Object.keys(overrides);
                const avgOverride = ovKeys.length ? Math.round(ovKeys.reduce((s, k) => s + (Number(overrides[k]) || 0), 0) / ovKeys.length) : null;
                const crPerDollar = p.price_usd > 0 ? Math.round(p.credits / p.price_usd) : null;
                const warn = (includedCount === 0) || (p.enabled && p.price_usd > 0 && crPerDollar !== null && crPerDollar < 50);
                return (
                  <tr key={p.id} className={`border-b border-white/5 ${warn ? "bg-amber-500/[0.04]" : ""}`} data-testid={`plans-audit-row-${p.slug}`}>
                    <td className="py-2 pr-3 font-semibold text-slate-100">
                      {p.name}
                      {p.popular && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300">popular</span>}
                    </td>
                    <td className="py-2 pr-3 font-mono">${p.price_usd}</td>
                    <td className="py-2 pr-3 font-mono">{p.credits.toLocaleString()}</td>
                    <td className="py-2 pr-3 font-mono text-purple-300">{crPerDollar !== null ? crPerDollar.toLocaleString() : "—"}</td>
                    <td className="py-2 pr-3">{includedCount === totalTools ? <span className="text-emerald-300">All ({totalTools})</span> : `${includedCount} of ${totalTools}`}</td>
                    <td className="py-2 pr-3">{ovKeys.length || "—"}</td>
                    <td className="py-2 pr-3 font-mono">{avgOverride !== null ? `${avgOverride} cr` : "—"}</td>
                    <td className="py-2 pr-3">{p.monthly_credit_limit ? <span className="font-mono">{p.monthly_credit_limit.toLocaleString()}/mo</span> : <span className="text-slate-500">none</span>}</td>
                    <td className="py-2 pr-3">
                      {p.enabled ? <span className="text-emerald-300 text-xs">enabled</span> : <span className="text-rose-300 text-xs">disabled</span>}
                      {warn && <Lucide.AlertTriangle className="inline ml-2 text-amber-300" size={13} />}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!plans.length && <div className="text-xs text-slate-500 py-4">No plans yet.</div>}
        </div>
        <div className="text-[11px] text-slate-500 mt-3 flex items-center gap-2">
          <Lucide.AlertTriangle size={11} className="text-amber-300" /> highlights plans where no tools are included or cr/$ is below 50 — likely misconfigured.
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mt-6">
        {plans.map((p) => (
          <div key={p.id} className="bx-card p-5">
            <div className="bx-heading text-lg font-bold">{p.name}</div>
            <div className="text-xs text-slate-400">{p.best_for}</div>
            <div className="bx-heading text-3xl font-extrabold mt-3">${p.price_usd}</div>
            <div className="text-xs text-purple-300 font-mono">{p.credits.toLocaleString()} credits · {p.validity_days}d</div>
            <div className="text-[11px] text-slate-500 mt-1">
              Tools: {(p.tool_access || []).length ? `${p.tool_access.length} included` : "All access"}
              {Object.keys(p.credit_overrides || {}).length > 0 && ` · ${Object.keys(p.credit_overrides).length} overrides`}
              {p.monthly_credit_limit ? ` · ${p.monthly_credit_limit.toLocaleString()}/mo cap` : ""}
            </div>
            <div className="mt-3"><span className="bx-chip" style={{ fontSize: 10 }}>{p.enabled ? "enabled" : "disabled"}</span> {p.popular && <span className="bx-chip" style={{ fontSize: 10 }}>popular</span>}</div>
            <button data-testid={`admin-plan-edit-${p.slug}`} onClick={() => openEdit(p)} className="bx-btn-ghost text-xs mt-4">Edit</button>
          </div>
        ))}
      </div>
      {editing && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-5 overflow-auto">
          <div className="bx-card p-6 max-w-3xl w-full my-10">
            <div className="bx-heading text-lg font-bold">Edit: {editing.name}</div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="bx-input col-span-2" />
              <input value={editing.best_for} onChange={(e) => setEditing({ ...editing, best_for: e.target.value })} placeholder="Best for" className="bx-input col-span-2" />
              <input type="number" step="0.01" value={editing.price_usd} onChange={(e) => setEditing({ ...editing, price_usd: Number(e.target.value) })} placeholder="Price USD" className="bx-input" />
              <input type="number" value={editing.credits} onChange={(e) => setEditing({ ...editing, credits: Number(e.target.value) })} placeholder="Credits included" className="bx-input" />
              <input type="number" value={editing.validity_days} onChange={(e) => setEditing({ ...editing, validity_days: Number(e.target.value) })} placeholder="Validity (days)" className="bx-input" />
              <input type="number" min="1" value={editing.monthly_credit_limit || ""} onChange={(e) => setEditing({ ...editing, monthly_credit_limit: e.target.value })} placeholder="Rolling 30-day credit cap (optional)" className="bx-input" />
              <textarea rows={4} value={editing.features} onChange={(e) => setEditing({ ...editing, features: e.target.value })} placeholder="Features (one per line)" className="bx-input col-span-2 resize-y" />
              <div className="col-span-2 flex gap-4">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.enabled} onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} /> Enabled</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.popular} onChange={(e) => setEditing({ ...editing, popular: e.target.checked })} /> Popular</label>
              </div>

              {/* Tool access */}
              <div className="col-span-2 rounded-lg border border-purple-500/20 bg-purple-500/5 p-3">
                <div className="text-xs text-purple-300 font-semibold">AI Tool Access</div>
                <div className="mt-2 flex gap-4 text-sm">
                  <label className="flex items-center gap-2"><input type="radio" name="access" checked={editing._toolAccessMode === "all"} onChange={() => setEditing({ ...editing, _toolAccessMode: "all" })} /> All tools</label>
                  <label className="flex items-center gap-2"><input type="radio" name="access" checked={editing._toolAccessMode === "include"} onChange={() => setEditing({ ...editing, _toolAccessMode: "include" })} /> Specific tools only</label>
                </div>
                {editing._toolAccessMode === "include" && (
                  <div className="mt-3 max-h-64 overflow-auto bx-scrollbar grid grid-cols-2 gap-x-3 gap-y-1.5 pr-1">
                    {tools.map((t) => {
                      const checked = (editing.tool_access || []).includes(t.slug);
                      return (
                        <label key={t.slug} className="flex items-center gap-2 text-xs">
                          <input data-testid={`plan-tool-${t.slug}`} type="checkbox" checked={checked} onChange={() => toggleToolAccess(t.slug)} />
                          <span className="text-slate-300">{t.name}</span>
                          <span className="text-[10px] text-purple-300 ml-auto font-mono">{t.credit_cost}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Per-tool credit overrides — categorized accordion */}
              <div className="col-span-2 rounded-lg border border-cyan-500/20 bg-cyan-500/5 p-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-cyan-300 font-semibold">Per-Tool Credit Overrides</div>
                  <span className="text-[10px] text-slate-500">Leave blank to use the tool's default cost</span>
                </div>
                <div className="mt-3 space-y-2">
                  {Array.from(new Set(tools.map((t) => t.category))).sort().map((cat) => {
                    const inCat = tools.filter((t) => t.category === cat);
                    const setCount = inCat.filter((t) => (editing.credit_overrides || {})[t.slug] != null).length;
                    const isOpen = (editing._openCats || {})[cat];
                    return (
                      <details
                        key={cat}
                        open={isOpen}
                        onToggle={(e) => setEditing((ed) => ({ ...ed, _openCats: { ...(ed._openCats || {}), [cat]: e.currentTarget.open } }))}
                        className="rounded-lg bg-black/30 border border-white/5"
                      >
                        <summary className="cursor-pointer px-3 py-2 flex items-center justify-between text-sm">
                          <span className="text-slate-200">{cat}</span>
                          <span className="text-[10px] text-slate-500">{inCat.length} tools · {setCount} override{setCount === 1 ? "" : "s"}</span>
                        </summary>
                        <div className="px-3 pb-3 grid grid-cols-2 gap-x-3 gap-y-2">
                          {inCat.map((t) => (
                            <div key={t.slug} className="flex items-center gap-2 text-xs">
                              <span className="text-slate-300 flex-1 truncate" title={t.name}>{t.name}</span>
                              <input
                                data-testid={`plan-override-${t.slug}`}
                                type="number"
                                value={(editing.credit_overrides || {})[t.slug] ?? ""}
                                placeholder={String(t.credit_cost)}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  const next = { ...(editing.credit_overrides || {}) };
                                  if (v === "") delete next[t.slug];
                                  else next[t.slug] = Number(v);
                                  setEditing({ ...editing, credit_overrides: next });
                                }}
                                className="bx-input w-20 text-xs py-1.5"
                              />
                            </div>
                          ))}
                        </div>
                      </details>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="bx-btn-ghost">Cancel</button>
              <button data-testid="admin-plan-save" onClick={save} className="bx-btn-primary">Save</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export function AdminGateways() {
  const [gws, setGws] = useState([]);
  const [testing, setTesting] = useState(null); // {name, result}
  const load = () => api.get("/admin/gateways").then((r) => setGws(r.data));
  useEffect(() => { load(); }, []);
  const update = (name, patch) => setGws((all) => all.map((g) => g.name === name ? { ...g, ...patch } : g));
  const updateExtra = (name, key, value) => setGws((all) => all.map((g) => g.name === name ? { ...g, extra: { ...(g.extra || {}), [key]: value } } : g));
  const save = async (g) => {
    try {
      await api.put(`/admin/gateways/${g.name}`, {
        name: g.name,
        enabled: g.enabled,
        public_key: g.public_key,
        secret_key: g.secret_key,
        webhook_secret: g.webhook_secret,
        extra: g.extra || {},
      });
      toast.success(`${g.name} saved`);
      load();
    }
    catch { toast.error("Save failed"); }
  };
  const clearSecret = async (g, field) => {
    if (!window.confirm(`Clear ${field} for ${g.name}? This will revert to fallback mode.`)) return;
    try {
      await api.put(`/admin/gateways/${g.name}`, {
        name: g.name,
        enabled: g.enabled,
        public_key: g.public_key,
        secret_key: field === "secret_key" ? "__CLEAR__" : (g.secret_key || ""),
        webhook_secret: field === "webhook_secret" ? "__CLEAR__" : (g.webhook_secret || ""),
        extra: g.extra || {},
      });
      toast.success(`${field} cleared`);
      load();
    }
    catch { toast.error("Failed"); }
  };
  const runTest = async (name) => {
    setTesting({ name, result: null, loading: true });
    try {
      const { data } = await api.post(`/admin/gateways/${name}/test-webhook`);
      setTesting({ name, result: data, loading: false });
      if (data.ok) toast.success(`${name} webhook responded ${data.status_code}`);
      else toast.error(`${name} webhook failed (${data.status_code || "no response"})`);
    } catch (e) {
      setTesting({ name, result: { ok: false, response: e?.response?.data?.detail || String(e) }, loading: false });
      toast.error("Test failed");
    }
  };
  return (
    <AdminLayout>
      <h1 className="bx-heading text-3xl font-extrabold">Payment Gateways</h1>
      <p className="text-slate-400 text-sm mt-1">Configure API credentials for Razorpay, Stripe, and PayGlocal. Secrets are encrypted at rest. Use the <span className="text-purple-300">Test webhook</span> button to verify each endpoint is reachable & signature-verifying correctly.</p>
      <div className="grid lg:grid-cols-3 gap-5 mt-6">
        {gws.map((g) => (
          <div key={g.name} className="bx-card p-5">
            <div className="flex items-center justify-between">
              <div className="bx-heading text-lg font-bold capitalize">{g.name}</div>
              <label className="flex items-center gap-2 text-sm"><input data-testid={`gateway-enable-${g.name}`} type="checkbox" checked={g.enabled} onChange={(e) => update(g.name, { enabled: e.target.checked })} /> Enabled</label>
            </div>
            <div className="text-xs text-slate-500 mt-1">Status: {g.enabled ? <span className="text-emerald-400">Active</span> : <span className="text-amber-300">Disabled</span>}</div>
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-xs text-slate-400">Public / Publishable Key</label>
                <input value={g.public_key || ""} onChange={(e) => update(g.name, { public_key: e.target.value })} placeholder="pk_..." className="bx-input mt-1 bx-mono text-xs" />
              </div>
              <div>
                <label className="text-xs text-slate-400 flex items-center justify-between">
                  Secret Key
                  {g.secret_key === "********" && (
                    <button onClick={() => clearSecret(g, "secret_key")} className="text-[10px] text-rose-300 underline">clear</button>
                  )}
                </label>
                <input type="password" value={g.secret_key === "********" ? "" : (g.secret_key || "")} onChange={(e) => update(g.name, { secret_key: e.target.value })} placeholder={g.secret_key === "********" ? "•••••• (set, leave blank to keep)" : "sk_..."} className="bx-input mt-1 bx-mono text-xs" />
              </div>
              <div>
                <label className="text-xs text-slate-400 flex items-center justify-between">
                  Webhook Secret
                  {g.webhook_secret === "********" && (
                    <button onClick={() => clearSecret(g, "webhook_secret")} className="text-[10px] text-rose-300 underline">clear</button>
                  )}
                </label>
                <input type="password" value={g.webhook_secret === "********" ? "" : (g.webhook_secret || "")} onChange={(e) => update(g.name, { webhook_secret: e.target.value })} placeholder={g.webhook_secret === "********" ? "•••••• (set, leave blank to keep)" : "whsec_..."} className="bx-input mt-1 bx-mono text-xs" />
              </div>

              {g.name === "razorpay" && (
                <div className="rounded-lg border border-purple-500/20 bg-purple-500/5 p-3">
                  <div className="text-xs text-purple-300 font-semibold">Razorpay-specific</div>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    <div>
                      <label className="text-[11px] text-slate-400">Currency</label>
                      <select
                        data-testid="razorpay-currency"
                        value={(g.extra || {}).currency || "USD"}
                        onChange={(e) => updateExtra(g.name, "currency", e.target.value)}
                        className="bx-input mt-1 text-xs"
                      >
                        <option value="USD">USD ($)</option>
                        <option value="INR">INR (₹)</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[11px] text-slate-400">USD → INR rate</label>
                      <input
                        type="number" step="0.01"
                        data-testid="razorpay-inr-rate"
                        value={(g.extra || {}).usd_to_inr_rate ?? 83}
                        onChange={(e) => updateExtra(g.name, "usd_to_inr_rate", Number(e.target.value))}
                        className="bx-input mt-1 text-xs"
                      />
                    </div>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-2">Used only when currency = INR. Plan prices stored in USD are converted at this rate when creating the Razorpay order.</div>
                </div>
              )}

              <div className="text-xs text-slate-500">Webhook URL: <code className="text-purple-300">/api/webhooks/{g.name}</code></div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button data-testid={`gateway-save-${g.name}`} onClick={() => save(g)} className="bx-btn-primary flex-1 justify-center"><Lucide.Save size={14} /> Save</button>
              <button data-testid={`gateway-test-${g.name}`} onClick={() => runTest(g.name)} disabled={!g.enabled} className="bx-btn-ghost flex-1 justify-center">
                <Lucide.PlugZap size={14} /> Test webhook
              </button>
            </div>
            {testing?.name === g.name && (
              <div className="mt-3 rounded-lg border border-white/10 p-3 text-xs bg-black/30">
                {testing.loading ? (
                  <span className="text-slate-400"><Lucide.Loader2 size={12} className="inline animate-spin mr-1" /> Firing test event…</span>
                ) : (
                  <>
                    <div className={testing.result?.ok ? "text-emerald-400" : "text-rose-400"}>
                      {testing.result?.ok ? "✓ Webhook OK" : "✗ Webhook failed"} · HTTP {testing.result?.status_code || "—"}
                    </div>
                    <div className="text-slate-500 mt-1 bx-mono break-all">{testing.result?.endpoint}</div>
                    <div className="text-slate-400 mt-1 bx-mono break-all">{testing.result?.response}</div>
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}

export function AdminInvoices() {
  const [inv, setInv] = useState([]);
  useEffect(() => { api.get("/admin/gateway-invoices").then((r) => setInv(r.data)); }, []);
  return (
    <AdminLayout>
      <h1 className="bx-heading text-3xl font-extrabold">Invoices</h1>
      <div className="bx-card mt-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-xs uppercase tracking-wider text-slate-400">
            <tr><th className="text-left p-3">Date</th><th className="text-left p-3">User</th><th className="text-left p-3">Plan</th><th className="text-left p-3">Gateway</th><th className="text-right p-3">Amount</th><th className="text-right p-3">Status</th></tr>
          </thead>
          <tbody>
            {inv.map((i) => (
              <tr key={i.id} className="border-t border-white/5">
                <td className="p-3 text-slate-400">{new Date(i.created_at).toLocaleString()}</td>
                <td className="p-3 bx-mono text-xs text-slate-400">{i.user_id.slice(0, 10)}…</td>
                <td className="p-3">{i.plan_name}</td>
                <td className="p-3 capitalize">{i.gateway}</td>
                <td className="p-3 text-right">${i.amount_usd.toFixed(2)}</td>
                <td className="p-3 text-right"><span className="bx-chip" style={{ fontSize: 10 }}>{i.status}</span></td>
              </tr>
            ))}
            {!inv.length && <tr><td colSpan={6} className="p-6 text-center text-slate-500">No invoices yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}

export function AdminTransactions() {
  const [tx, setTx] = useState([]);
  useEffect(() => { api.get("/admin/transactions").then((r) => setTx(r.data)); }, []);
  return (
    <AdminLayout>
      <h1 className="bx-heading text-3xl font-extrabold">Transactions</h1>
      <div className="bx-card mt-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-xs uppercase tracking-wider text-slate-400">
            <tr><th className="text-left p-3">Date</th><th className="text-left p-3">User</th><th className="text-left p-3">Kind</th><th className="text-left p-3">Note</th><th className="text-right p-3">Amount</th></tr>
          </thead>
          <tbody>
            {tx.map((t) => (
              <tr key={t.id} className="border-t border-white/5">
                <td className="p-3 text-slate-400">{new Date(t.created_at).toLocaleString()}</td>
                <td className="p-3 bx-mono text-xs">{t.user_id?.slice(0, 10)}…</td>
                <td className="p-3"><span className="bx-chip" style={{ fontSize: 10 }}>{t.kind}</span></td>
                <td className="p-3">{t.note}</td>
                <td className={`p-3 text-right font-mono ${t.amount >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{t.amount >= 0 ? "+" : ""}{t.amount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
